import React, { useState, useMemo, useEffect, ReactElement, useRef } from 'react';
import { FormControl, InputAdornment, OutlinedInput, Typography } from '@material-ui/core';
import debounce from 'lodash/debounce';

interface DebouncedInputProps {
	id: string;
	value: string;
	type?: 'text' | 'number';
	label?: string;
	className?: string;
	onChange: (_value: string) => void;
	debounceTime?: number;
	placeholder?: string;
	endIcon?: ReactElement,
}

const DebouncedInput = ({
	id,
	value = '',
	label,
	type = 'text',
	className,
	onChange,
	debounceTime = 300,
	placeholder,
	endIcon,
}: DebouncedInputProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState<string>(value);

    useEffect(() => {
    	setInputValue(value);
    }, [value]);

    // Use debounce to avoid unnecessary parent component re-renders
    const debouncedOnChange = useMemo(
		() =>
			debounce((value: string) => {
				onChange(value);
			}, debounceTime),
		[onChange, debounceTime]
    );

    // Clear debounce on unmount to prevent memory leaks
    useEffect(() => {
		return () => {
			debouncedOnChange.cancel();
		};
    }, [debouncedOnChange]);

	// Prevents the infinite increment (decrement) bug in Chrome
	// by losing focus on the input when a browser stepper is clicked
	const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
		const input = inputRef.current;
		if (!input) {
			return;
		}

		const rect = input.getBoundingClientRect();
		const clickX = e.clientX - rect.left;

		const stepperZoneStart = rect.width - 30;
		if (clickX >= stepperZoneStart) {
			setTimeout(() => input.blur(), 100);
		}
	};

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		requestAnimationFrame(() => {
			setInputValue(val);
			debouncedOnChange(val);
		});
    };

    return (
		<FormControl className={className} fullWidth>
			{label && <Typography
				variant="body2"
				style={{ marginBottom: 10, fontSize: 12 }}
				component="label"
				color="textPrimary"
				htmlFor={id}
			>
				{ label }
			</Typography>}
			<OutlinedInput
				id={id}
				type={type}
				inputRef={inputRef}
				placeholder={placeholder}
				style={{ fontSize: 12, height: 42 }}
				value={inputValue}
				onChange={handleChange}
				onMouseDown={handleMouseDown}
				endAdornment={endIcon &&
					<InputAdornment position="end">
						{endIcon}
					</InputAdornment>
				}
			/>
		</FormControl>
    );
};

export default DebouncedInput;
